import * as React from 'react';

import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { Card, Chart } from '@rematter/ui-web';
import { differenceInDays } from 'date-fns';
import { useAtomValue } from 'jotai';

import {
  $overviewPendingBills,
  $overviewPendingInvoices,
} from '../../_state/financeOverview.state';

import { useTranslations } from '@/finance/i18n/useTranslations';

const getDaysOverdue = (
  dueDate: CalendarDate,
  todayDate: CalendarDate,
): number => {
  const dueDateJs = dueDate.toDate(getLocalTimeZone());
  const todayJs = todayDate.toDate(getLocalTimeZone());
  return differenceInDays(todayJs, dueDateJs);
};

const getBucketIndex = (daysOverdue: number): number => {
  if (daysOverdue <= 0) return 0;
  if (daysOverdue <= 30) return 1;
  if (daysOverdue <= 60) return 2;
  if (daysOverdue <= 90) return 3;
  return 4;
};

const getDominantCurrency = (
  items: Array<{ amount: { amount: number; currencyCode: string } }>,
): string | null => {
  const totals: Record<string, number> = {};
  for (const item of items) {
    const code = item.amount.currencyCode;
    totals[code] = (totals[code] ?? 0) + Math.abs(item.amount.amount);
  }
  const entries = Object.entries(totals);
  if (entries.length === 0) return null;
  return entries.sort((a, b) => b[1] - a[1])[0][0];
};

export const OverviewAgingChart = () => {
  const t = useTranslations();
  const { data: pendingBillsData } = useAtomValue($overviewPendingBills);
  const { data: pendingInvoicesData } = useAtomValue($overviewPendingInvoices);

  const pendingBills = React.useMemo(
    () => pendingBillsData?.bills ?? [],
    [pendingBillsData],
  );

  const pendingInvoices = React.useMemo(
    () => pendingInvoicesData?.invoices ?? [],
    [pendingInvoicesData],
  );

  const apCurrency = React.useMemo(
    () => getDominantCurrency(pendingBills),
    [pendingBills],
  );

  const arCurrency = React.useMemo(
    () => getDominantCurrency(pendingInvoices),
    [pendingInvoices],
  );

  const apLabel = t.overview.aging.series.ap;
  const arLabel = t.overview.aging.series.ar;

  const chartData = React.useMemo(() => {
    const todayDate = today(getLocalTimeZone());
    const bucketLabels = [
      t.overview.aging.buckets.current,
      t.overview.aging.buckets.days1to30,
      t.overview.aging.buckets.days31to60,
      t.overview.aging.buckets.days61to90,
      t.overview.aging.buckets.days90plus,
    ];

    const ap = [0, 0, 0, 0, 0];
    const ar = [0, 0, 0, 0, 0];

    for (const bill of pendingBills) {
      if (!bill.dueDate) continue;
      if (apCurrency && bill.amount.currencyCode !== apCurrency) continue;
      const daysOverdue = getDaysOverdue(bill.dueDate, todayDate);
      const idx = getBucketIndex(daysOverdue);
      ap[idx] += bill.amount.amount;
    }

    for (const invoice of pendingInvoices) {
      if (!invoice.dueDate) continue;
      if (arCurrency && invoice.amount.currencyCode !== arCurrency) continue;
      const daysOverdue = getDaysOverdue(invoice.dueDate, todayDate);
      const idx = getBucketIndex(daysOverdue);
      ar[idx] += invoice.amount.amount;
    }

    return bucketLabels.map((label, i) => ({
      bucket: label,
      [apLabel]: ap[i],
      [arLabel]: ar[i],
    }));
  }, [
    pendingBills,
    pendingInvoices,
    apCurrency,
    arCurrency,
    apLabel,
    arLabel,
    t,
  ]);

  const hasData = React.useMemo(
    () =>
      pendingBills.some((b) => b.dueDate !== null) ||
      pendingInvoices.some((inv) => inv.dueDate !== null),
    [pendingBills, pendingInvoices],
  );

  const hasMultipleCurrencies = React.useMemo(() => {
    const billCurrencies = new Set(
      pendingBills.map((b) => b.amount.currencyCode),
    );
    const invoiceCurrencies = new Set(
      pendingInvoices.map((inv) => inv.amount.currencyCode),
    );
    return billCurrencies.size > 1 || invoiceCurrencies.size > 1;
  }, [pendingBills, pendingInvoices]);

  const subtitleText = hasMultipleCurrencies
    ? `${t.overview.aging.subtitle} (${[apCurrency, arCurrency].filter(Boolean).join('/')})`
    : t.overview.aging.subtitle;

  return (
    <Card className="@OverviewAgingChart flex h-full flex-col">
      <h3 className="text-lg font-semibold">{t.overview.aging.title}</h3>
      <p className="text-sm text-neutral">{subtitleText}</p>
      {hasData ? (
        <Chart
          type="column"
          data={chartData}
          xKey="bucket"
          yKey={`${apLabel},${arLabel}`}
        />
      ) : (
        <p className="py-8 text-center text-sm text-neutral">
          {t.overview.aging.empty}
        </p>
      )}
    </Card>
  );
};
